import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@config/axios/axios.config";
import { SERVER_ROUTES, QUERY_KEYS } from "@constants/api.routes";
import { showNotification } from "@libs/notistack/notificationStack";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import type {
	EmploymentActivityResponse,
	HigherStudyResponse,
	StudentEmploymentPayload,
} from "./statusService";

const invalidateEmploymentStatusQueries = async (queryClient: QueryClient) => {
	await Promise.all([
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.STATUS.EMPLOYMENT_ACTIVITY],
			refetchType: "active",
		}),
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.STATUS.OUTCOME_CURRENT],
			refetchType: "active",
		}),
	]);
};

const invalidateHigherStudyStatusQueries = async (queryClient: QueryClient) => {
	await Promise.all([
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.STATUS.HIGHER_STUDY],
			refetchType: "active",
		}),
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.STATUS.OUTCOME_CURRENT],
			refetchType: "active",
		}),
	]);
};

export interface StudentOutcomePayload {
	outcomeStatusId: string;
	effectiveFrom: string;
	remarks?: string;
	suppressSuccessNotification?: boolean;
}

export interface StudentOutcomeResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data?: unknown;
}

export const useUpdateStudentOutcome = () => {
	const queryClient = useQueryClient();

	return useMutation<StudentOutcomeResponse, Error, StudentOutcomePayload>({
		mutationFn: async variables => {
			const { outcomeStatusId, effectiveFrom, remarks } = variables;
			try {
				const response = await api.post<StudentOutcomeResponse>(SERVER_ROUTES.STUDENT.OUTCOME, {
					outcomeStatusId,
					effectiveFrom,
					remarks,
				});
				return response as unknown as StudentOutcomeResponse;
			} catch (error) {
				console.error("Error updating student outcome:", error);
				throw new Error("Failed to update student outcome. Please try again.");
			}
		},
		onSuccess: (response, variables) => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.STATUS.OUTCOME_CURRENT],
				refetchType: "active",
			});
			if (response?.success && !variables.suppressSuccessNotification) {
				showNotification({
					message: response.message || "Status updated successfully",
					severity: SNACKBAR_SEVERITY.SUCCESS,
				});
			}
		},
	});
};

export type EmploymentActivityPayload = StudentEmploymentPayload;

export const useCreateEmploymentActivity = () => {
	const queryClient = useQueryClient();

	return useMutation<EmploymentActivityResponse, Error, EmploymentActivityPayload>({
		mutationFn: async (payload: EmploymentActivityPayload) => {
			try {
				const response = await api.post<EmploymentActivityResponse>(SERVER_ROUTES.STUDENT.EMPLOYMENTS, payload);
				return response as unknown as EmploymentActivityResponse;
			} catch (error) {
				console.error("Error creating employment activity:", error);
				throw new Error("Failed to create employment activity. Please try again.");
			}
		},
		onSuccess: async response => {
			await invalidateEmploymentStatusQueries(queryClient);
			if (response?.success) {
				showNotification({
					message: response.message || "Employment details updated successfully",
					severity: SNACKBAR_SEVERITY.SUCCESS,
				});
			}
		},
	});
};

export const useUpdateEmploymentActivity = () => {
	const queryClient = useQueryClient();

	return useMutation<EmploymentActivityResponse, Error, { uuid: string; payload: EmploymentActivityPayload }>({
		mutationFn: async ({ uuid, payload }) => {
			try {
				const response = await api.put<EmploymentActivityResponse>(
					SERVER_ROUTES.STUDENT.EMPLOYMENT_BY_UUID(uuid),
					payload,
				);
				return response as unknown as EmploymentActivityResponse;
			} catch (error) {
				console.error("Error updating employment activity:", error);
				throw new Error("Failed to update employment activity. Please try again.");
			}
		},
		onSuccess: async response => {
			await invalidateEmploymentStatusQueries(queryClient);
			if (response?.success) {
				showNotification({
					message: response.message || "Employment details updated successfully",
					severity: SNACKBAR_SEVERITY.SUCCESS,
				});
			}
		},
	});
};

export interface DeleteResponse {
	success: boolean;
	statusCode: number;
	message: string;
}

export const useDeleteEmploymentActivity = () => {
	const queryClient = useQueryClient();

	return useMutation<DeleteResponse, Error, string>({
		mutationFn: async (id: string) => {
			try {
				const response = await api.delete<DeleteResponse>(
					SERVER_ROUTES.STUDENT.EMPLOYMENT_BY_UUID(id),
				);
				return response as unknown as DeleteResponse;
			} catch (error) {
				console.error("Error deleting employment activity:", error);
				throw new Error("Failed to delete employment activity. Please try again.");
			}
		},
		onSuccess: async response => {
			await invalidateEmploymentStatusQueries(queryClient);
			if (response?.success) {
				showNotification({
					message: response.message || "Employment deleted successfully",
					severity: SNACKBAR_SEVERITY.SUCCESS,
				});
			}
		},
	});
};

export interface HigherStudyPayload {
	instituteName: string;
	contactPersonName: string;
	contactPersonNumber: string;
	degreeName: string;
}

export const useCreateHigherStudy = () => {
	const queryClient = useQueryClient();

	return useMutation<HigherStudyResponse, Error, HigherStudyPayload>({
		mutationFn: async (payload: HigherStudyPayload) => {
			try {
				const response = await api.post<HigherStudyResponse>(SERVER_ROUTES.INSTITUTES.HIGHER_STUDY, payload);
				return response as unknown as HigherStudyResponse;
			} catch (error) {
				console.error("Error creating higher study:", error);
				throw new Error("Failed to create higher study. Please try again.");
			}
		},
		onSuccess: async response => {
			await invalidateHigherStudyStatusQueries(queryClient);
			if (response?.success && response.message) {
				showNotification({
					message: response.message,
					severity: SNACKBAR_SEVERITY.SUCCESS,
				});
			}
		},
	});
};

export const useUpdateHigherStudy = () => {
	const queryClient = useQueryClient();

	return useMutation<HigherStudyResponse, Error, { uuid: string; payload: HigherStudyPayload }>({
		mutationFn: async ({ uuid, payload }) => {
			try {
				const response = await api.put<HigherStudyResponse>(
					SERVER_ROUTES.INSTITUTES.HIGHER_STUDY_BY_UUID(uuid),
					payload,
				);
				return response as unknown as HigherStudyResponse;
			} catch (error) {
				console.error("Error updating higher study:", error);
				throw new Error("Failed to update higher study. Please try again.");
			}
		},
		onSuccess: async response => {
			await invalidateHigherStudyStatusQueries(queryClient);
			if (response?.success && response.message) {
				showNotification({
					message: response.message,
					severity: SNACKBAR_SEVERITY.SUCCESS,
				});
			}
		},
	});
};

export const useDeleteHigherStudy = () => {
	const queryClient = useQueryClient();

	return useMutation<DeleteResponse, Error, string>({
		mutationFn: async (uuid: string) => {
			try {
				const response = await api.delete<DeleteResponse>(
					SERVER_ROUTES.INSTITUTES.HIGHER_STUDY_BY_UUID(uuid),
				);
				return response as unknown as DeleteResponse;
			} catch (error) {
				console.error("Error deleting higher study:", error);
				throw new Error("Failed to delete higher study. Please try again.");
			}
		},
		onSuccess: async response => {
			await invalidateHigherStudyStatusQueries(queryClient);
			if (response?.success) {
				showNotification({
					message: response.message || "Higher study deleted successfully",
					severity: SNACKBAR_SEVERITY.SUCCESS,
				});
			}
		},
	});
};
